import { PostSource, PostStatus } from './constants';
import type { BusinessLiteDto, BusinessModel } from '../business/types';

type ClickOutDto = {
    url: string;
    url_display: string;
    target: 'button';
    text: string;
};

export enum ReactionState {
    LIKE = 'like',
    DISLIKE = 'dislike',
    NONE = 'none',
}

export type Content<T extends 'dto' | 'model' = 'dto'> =
    | {
          id: string;
          type: 'image';
          media: Inspire.Image[];
      }
    | {
          id: string;
          type: 'video';
          media: T extends 'dto' ? Inspire.VideoDto[] : Inspire.Video[];
      };

export interface PriceDto {
    currency: string;
    integer_part: number;
    fractional_part?: number;
}

export interface ProductItemDto {
    id: string;
    url: string;
    title: string;
    content: Content[];
    price_info: PriceDto;
    old_price_info?: PriceDto;
}

export interface ProductsDto {
    items: ProductItemDto[];
    button_title: string;
    business_button_title: string;
    description: string;
    title: string;
}

export interface PriceModel {
    currency: string;
    integerPart: number;
    fractionalPart?: number;
}

export type ProductItemModel = Omit<ProductItemDto, 'price_info' | 'old_price_info' | 'content'> & {
    content: Content<'model'>[];
    priceInfo: PriceModel;
    oldPriceInfo: PriceModel;
};

export type ProductsModel = Omit<
    ProductsDto,
    'items' | 'button_title' | 'business_button_title'
> & {
    items: ProductItemModel[];
    buttonTitle: string;
    businessButtonTitle: string;
};

export interface PostDto {
    id: string;
    text: string;
    likes: number;
    views: number;
    reaction_state: ReactionState;
    saved: boolean;
    tags: string[];
    status: PostStatus;
    post_url: string;
    data_source: PostSource;
    business?: BusinessLiteDto;
    clickouts: ClickOutDto[];
    content: Content[];
    created_at: string;
    dj_request_id?: string;
    products_info?: ProductsDto;
    post_type?: 'post' | 'product' | 'collection';
    owners_data?: {
        author: {
            id: string;
            display_name: string;
            avatar_url: Inspire.Image[];
        };
        shop: {
            display_url: string;
        };
        is_owner: boolean;
    };
}

type ClickOutModel = {
    url: string;
    urlDisplay: string;
    target: 'button';
    text: string;
};

export interface PostModel
    extends Omit<
        PostDto,
        | 'business'
        | 'categories'
        | 'content'
        | 'created_at'
        | 'clickouts'
        | 'data_source'
        | 'post_url'
        | 'reaction_state'
        | 'products'
        | 'dj_request_id'
        | 'post_type'
        | 'owners_data'
        | 'products_info'
    > {
    createdAt: string;
    url: string;
    source: PostSource;
    shareUrl: string;
    djRequestId?: string;
    clickouts: ClickOutModel[];
    business?: BusinessModel;
    reactionState: ReactionState;
    content: Content<'model'>[];
    products: ProductsModel;
    postType?: 'post' | 'product' | 'collection';
    ownersData?: {
        author: {
            id: string;
            displayName: string;
            avatarUrl: Inspire.Image[];
        };
        shop: {
            displayUrl: string;
        };
        isOwner: boolean;
    };
}

export interface PostDataModel extends Omit<PostModel, 'business' | 'categories'> {
    business?: BusinessModel['id'];
}

export interface PostPersistenceDto {
    business_id?: string;
    text: string;
    media?: Array<{
        id: string;
        url: string;
        type: 'image' | 'video';
    }>;
    status?: PostStatus;
    data_source: PostSource;
    clickouts: Array<{
        url: string;
        url_display: string;
        text: string;
        target: 'button';
    }>;
    product_ids: string[];
}

export interface PostPersistenceModel {
    id?: string;
    text: string;
    businessId?: string;
    status?: PostStatus;
    source?: PostSource;
    media?: Array<{
        id: string;
        url: string;
        type: 'image' | 'video';
    }>;
    clickouts: Array<{
        url: string;
        urlDisplay: string;
        text: string;
        target: 'button';
    }>;
    productIds: string[];
}

export enum ReportReasons {
    OTHER = '0',
    VIOLENCE = '1',
    TRAGIC_CONTENT = '2',
    OFFENSIVE_REMARKS = '3',
    ADULT_CONTENT = '4',
    FRAUD = '5',
    SPAM_AND_ILLEGAL_CONTENT = '6',
    SELF_MEDICATION = '7',
    VIOLATION_OF_LAW = '8',
    CONTENT_INCONSISTENCY = '9',
}

export interface PostStatItem {
    value: number;
    display: string;
}

export interface PostStatModel {
    coverage: {
        total: PostStatItem;
        subscribers: PostStatItem;
        notsubscribers: PostStatItem;
    };
    involved: {
        total: PostStatItem;
        subscribers: PostStatItem;
        notsubscribers: PostStatItem;
    };
    shows: {
        total: PostStatItem;
        subscribers: PostStatItem;
        notsubscribers: PostStatItem;
    };
    subscribers: {
        total: PostStatItem;
        subscribed: PostStatItem;
        unsubscribed: PostStatItem;
    };
    interactions: {
        total: PostStatItem;
        clicks: PostStatItem;
        likes: PostStatItem;
        shares: PostStatItem;
        saves: PostStatItem;
    };
}

export interface PostStatDto extends PostStatModel {}
